import type { ColumnEntity, DefaultColumn } from './schemas';

/**
 * Readers over the subtype union. Only an ordinary column is styleable — a
 * header slot carries two knobs and a zero column none — so callers that just
 * want "how wide is this column" would otherwise have to narrow the union at
 * every site.
 */
export function columnWidth(column: Pick<ColumnEntity, 'props'>): number {
    const { props } = column;

    return 'width' in props ? props.width.value : 0;
}

/** `col` for twelfths of the grid, `%` for a share of the row. */
export function columnWidthUnit(column: Pick<ColumnEntity, 'props'>): 'col' | '%' {
    const { props } = column;

    return 'width' in props ? props.width.unit : 'col';
}

/** Twelfths of the row — the denominator of the `col` unit. */
const GRID_UNITS = 12;

/**
 * Converts a **whole row's** widths between the two units. The row owns the
 * choice, so this runs when the user flips its `isFlexibleWidths` switch — with
 * one stored number per column the old values have to be carried across rather
 * than left behind in a sibling field.
 *
 * It takes the row rather than a single width because the grid is coarse: only
 * multiples of a twelfth are expressible, so rounding each column on its own
 * would leave the row short (20/20/60% rounds to 2+2+7 = 11 of 12). The leftover
 * is spread a twelfth at a time, widest column first — that is where a twelfth
 * is least visible, and one column never absorbs the whole remainder: five equal
 * columns are two twelfths short, and dumping both on one of them would show as
 * a column of double width in a row the user asked to be even.
 *
 * Going the other way needs no balancing — a twelfth is exactly 8⅓ percent, so
 * nothing is rounded and whatever the row summed to carries over.
 */
export function convertColumnWidths(values: number[], to: 'col' | '%'): number[] {
    if (to === '%') {
        return values.map((value) => (value / GRID_UNITS) * 100);
    }

    // A column may never round away to nothing, however narrow it was.
    const rounded = values.map((value) => Math.max(1, Math.round((value / 100) * GRID_UNITS)));
    // Ranked once, off the rounded widths, so each pass hands out at most one
    // twelfth per column instead of re-picking the same column it just widened.
    const widestFirst = rounded.map((_, index) => index).sort((a, b) => rounded[b] - rounded[a] || a - b);

    let remainder = GRID_UNITS - rounded.reduce((sum, value) => sum + value, 0);
    // A row of more than twelve columns cannot be balanced at all — every column
    // is already at the one twelfth it may not go below — so the loop stops on
    // the pass that changes nothing rather than spinning.
    let changed = true;

    while (remainder !== 0 && changed) {
        changed = false;

        for (const index of widestFirst) {
            if (remainder === 0) {
                break;
            }

            if (remainder > 0) {
                rounded[index] += 1;
                remainder -= 1;
                changed = true;
            } else if (rounded[index] > 1) {
                rounded[index] -= 1;
                remainder += 1;
                changed = true;
            }
        }
    }

    return rounded;
}

/** `left | center | right`; `null` when the row decides instead. */
export function columnHorizontalAlign(column: Pick<ColumnEntity, 'props'>): string | null {
    const { props } = column;

    return 'horizontalAlign' in props ? (props.horizontalAlign ?? null) : null;
}

/** `top | middle | bottom`; `null` when the row decides instead. */
export function columnVerticalAlign(column: Pick<ColumnEntity, 'props'>): string | null {
    const { props } = column;

    return 'verticalAlign' in props ? (props.verticalAlign ?? null) : null;
}

/** The entrance animation, or `undefined` for a subtype that cannot carry one. */
export function columnAnimation(column: Pick<ColumnEntity, 'props'>): DefaultColumn['props']['animation'] | undefined {
    const { props } = column;

    return 'animation' in props ? props.animation : undefined;
}

/** Whether the column has an entrance animation — the zero-block host asks a lot. */
export function columnIsAnimated(column: Pick<ColumnEntity, 'props'>): boolean {
    return Boolean(columnAnimation(column)?.isActive);
}
